import {type DomainMaybe} from './../types';

/**
 * Calculate domain using pre-calculated min/max bounds from backend.
 * This avoids iterating through all points when BackendLinesQuery provides the bounds.
 */
export function calculateDomainFromLineData(
  filteredLines: {
    domain?: {minX: number; maxX: number; minY: number; maxY: number};
  }[],
  retXDomain: DomainMaybe,
  retYDomain: DomainMaybe,
  isHeatmap: boolean
): readonly [DomainMaybe, DomainMaybe] {
  // Aggregate min/max across all lines
  let xMin = Infinity;
  let xMax = -Infinity;
  let yMin = Infinity;
  let yMax = -Infinity;

  for (let i = 0; i < filteredLines.length; i++) {
    const domain = filteredLines[i].domain!;

    // Check for null/undefined values - if any domain value is null, we can't use this fast path
    if (
      domain.minX == null ||
      domain.maxX == null ||
      domain.minY == null ||
      domain.maxY == null
    ) {
      // Return null domain to trigger fallback to point iteration
      return [
        [null, null] as DomainMaybe,
        [null, null] as DomainMaybe,
      ] as const;
    }

    if (domain.minX < xMin && Number.isFinite(domain.minX)) {
      xMin = domain.minX;
    }
    if (domain.maxX > xMax && Number.isFinite(domain.maxX)) {
      xMax = domain.maxX;
    }
    if (domain.minY < yMin && Number.isFinite(domain.minY)) {
      yMin = domain.minY;
    }
    if (domain.maxY > yMax && Number.isFinite(domain.maxY)) {
      yMax = domain.maxY;
    }
  }

  const xRange = xMax - xMin;
  const yRange = yMax - yMin;

  // Handle zero-range X values (same as calculateDomain)
  if (xRange === 0) {
    if (isHeatmap) {
      // ridiculous hacky fix to single step heatmap
      xMin = -0.49998;
      xMax = 0.49999;
    } else {
      xMin -= Math.abs(xMin);
      xMax += xMax === 0 ? 1 : Math.abs(xMax);
    }
  }

  // Handle zero-range Y values (same as calculateDomain)
  if (yRange < 1e-15 && Number.isFinite(yRange)) {
    yMin -= yMax === 0 ? 2 : Math.abs(yMin);
    yMax += yMax === 0 ? 2 : Math.abs(yMax);
  }

  // Apply user domain overrides
  retXDomain[0] = retXDomain[0] ?? xMin;
  retXDomain[1] = retXDomain[1] ?? xMax;
  retYDomain[0] = retYDomain[0] ?? yMin;
  retYDomain[1] = retYDomain[1] ?? yMax;

  return [retXDomain, retYDomain] as const;
}
